import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Award, 
  Download, 
  Plus, 
  Search, 
  Filter,
  GraduationCap,
  Star,
  TrendingUp,
  X,
  Edit,
  Eye,
  Layout,
  CheckCircle2,
  Maximize2,
  Trash2,
  AlertTriangle,
  BookOpen
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';
import { 
  generateReportCard,
  getReportCards,
  updateReportCard,
  deleteReportCard,
  generateCertificate,
  getCertificates,
  updateCertificate,
  deleteCertificate,
  getBatches,
  getBatchEnrollments,
  updateUserProfile
} from '../../firebase/collections';
import { getHolisticReportCards, deleteHolisticReportCard, updateHolisticReportCard } from '../../firebase/reportCards';
import { collection, query, where, orderBy, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HolisticReportCardForm from '../../components/coaching/HolisticReportCardForm';

const ReportCards = () => {
  const { userProfile, currentUser } = useRealtime();
  const [activeTab, setActiveTab] = useState('report-cards');
  const [loading, setLoading] = useState(true);
  const [reportCards, setReportCards] = useState([]);
  const [holisticReportCards, setHolisticReportCards] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null); // Main batch selection for viewing data
  const [selectedBatchForReportCard, setSelectedBatchForReportCard] = useState(null);
  const [selectedBatchForCertificate, setSelectedBatchForCertificate] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  const coachingId = userProfile?.coachingId || null;
  
  // Form states
  const [showReportCardForm, setShowReportCardForm] = useState(false);
  const [showHolisticReportCardForm, setShowHolisticReportCardForm] = useState(false);
  const [editingHolisticReport, setEditingHolisticReport] = useState(null);
  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [selectedReportCard, setSelectedReportCard] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null, type: null });
  const [instituteDefaultsForm, setInstituteDefaultsForm] = useState({
    address: '',
    affiliationNumber: '',
    instituteEmail: '',
    institutePhone: ''
  });
  const [savingInstituteDefaults, setSavingInstituteDefaults] = useState(false);

  const [reportCardForm, setReportCardForm] = useState({
    studentId: '',
    batchId: '',
    academicYear: new Date().getFullYear(),
    term: '',
    subjects: [{ name: '', marks: '', maxMarks: '' }],
    remarks: '',
    templateId: 'modern-clean', // Default template
    teacherName: '',
    principalName: '',
    rollNo: '',
    attendance: ''
  });

  const [certificateForm, setCertificateForm] = useState({
    studentId: '',
    certificateType: 'achievement',
    title: '',
    description: '',
    achievementDate: new Date().toISOString().split('T')[0],
    templateId: 'classic-gold', // Default template
    issuerName: '',
    issuerTitle: '',
    venue: ''
  });

  const reportCardTemplates = [
    { id: 'modern-clean', name: 'Modern Clean', preview: 'https://placehold.co/150x200/3b82f6/white?text=Modern+Clean' },
    { id: 'academic-classic', name: 'Academic Classic', preview: 'https://placehold.co/150x200/1e293b/white?text=Academic+Classic' },
    { id: 'professional-grid', name: 'Professional Grid', preview: 'https://placehold.co/150x200/0f172a/white?text=Professional+Grid' }
  ];

  const certificateTemplates = [
    { id: 'classic-gold', name: 'Classic Gold', preview: 'https://placehold.co/200x150/d4af37/white?text=Classic+Gold' },
    { id: 'modern-blue', name: 'Modern Blue', preview: 'https://placehold.co/200x150/3b82f6/white?text=Modern+Blue' },
    { id: 'minimalist-pro', name: 'Minimalist Pro', preview: 'https://placehold.co/200x150/f8fafc/slate?text=Minimalist+Pro' }
  ];

  const rcInstituteDefaults = userProfile?.reportCardInstituteDefaults;

  useEffect(() => {
    const d = rcInstituteDefaults || {};
    setInstituteDefaultsForm({
      address: d.address || '',
      affiliationNumber: d.affiliationNumber || '',
      instituteEmail: d.instituteEmail || '',
      institutePhone: d.institutePhone || ''
    });
  }, [
    userProfile?.uid,
    rcInstituteDefaults?.address,
    rcInstituteDefaults?.affiliationNumber,
    rcInstituteDefaults?.instituteEmail,
    rcInstituteDefaults?.institutePhone
  ]);

  const handleSaveInstituteReportDefaults = async () => {
    if (!currentUser?.uid) {
      toast.error('You must be signed in to save');
      return;
    }
    setSavingInstituteDefaults(true);
    try {
      const reportCardInstituteDefaults = {
        address: instituteDefaultsForm.address.trim(),
        affiliationNumber: instituteDefaultsForm.affiliationNumber.trim(),
        instituteEmail: instituteDefaultsForm.instituteEmail.trim(),
        institutePhone: instituteDefaultsForm.institutePhone.trim()
      };
      const result = await updateUserProfile(currentUser.uid, { reportCardInstituteDefaults });
      if (result.success) {
        toast.success('Institute details saved. They will appear on all report cards.');
      } else {
        toast.error(result.error?.message || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save institute details');
    } finally {
      setSavingInstituteDefaults(false);
    }
  };

  const loadBatches = async () => {
    try {
      const coachingId = userProfile?.coachingId;
      const batchesResult = await getBatches(coachingId);
      if (batchesResult.success) setBatches(batchesResult.data || []);
    } catch (error) {
      toast.error('Failed to load batches');
    }
  };

  useEffect(() => {
    if (coachingId) {
      loadBatches();
    } else {
      setLoading(false);
    }
  }, [coachingId]);

  // Load data when batch is selected
  useEffect(() => {
    if (coachingId && selectedBatch) {
      loadData();
    } else {
      // Clear data when no batch is selected
      setReportCards([]);
      setHolisticReportCards([]);
      setCertificates([]);
      setLoading(false);
    }
  }, [coachingId, selectedBatch]);

  const loadData = async () => {
    const coachingId = userProfile?.coachingId;
    const batchId = selectedBatch;
    if (!coachingId || !batchId) {
      setLoading(false);
      return;
    }

    console.log('Loading data for batch:', batchId, 'coachingId:', coachingId);
    setLoading(true);
    try {
      // Load report cards
      let reportCardsSnapshot;
      try {
        const reportCardsQuery = query(
          collection(db, COLLECTIONS.REPORT_CARDS),
          where('coachingId', '==', coachingId),
          where('batchId', '==', batchId),
          orderBy('generatedAt', 'desc')
        );
        reportCardsSnapshot = await getDocs(reportCardsQuery);
      } catch (error) {
        // If index error, try fallback query without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.log('Index not available, using fallback query for report cards');
          const fallbackQuery = query(
            collection(db, COLLECTIONS.REPORT_CARDS),
            where('coachingId', '==', coachingId),
            where('batchId', '==', batchId)
          );
          reportCardsSnapshot = await getDocs(fallbackQuery);
        } else {
          throw error;
        }
      }
      console.log('Report cards snapshot size:', reportCardsSnapshot.docs.length);
      const cards = [];
      for (const docSnap of reportCardsSnapshot.docs) {
        const cardData = { id: docSnap.id, ...docSnap.data() };
        // Get student details
        try {
          const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, cardData.studentId));
          if (userDoc.exists()) {
            cardData.studentData = userDoc.data();
          }
        } catch (err) {
          console.warn('Student not found for report card:', cardData.studentId, err);
        }
        cards.push(cardData);
      }
      // Sort by generatedAt if not already sorted
      cards.sort((a, b) => {
        const dateA = a.generatedAt?.toDate ? a.generatedAt.toDate() : new Date(a.generatedAt || 0);
        const dateB = b.generatedAt?.toDate ? b.generatedAt.toDate() : new Date(b.generatedAt || 0);
        return dateB - dateA;
      });
      setReportCards(cards);

      // Load certificates
      let certificatesSnapshot;
      try {
        const certificatesQuery = query(
          collection(db, COLLECTIONS.CERTIFICATES),
          where('coachingId', '==', coachingId),
          where('batchId', '==', batchId),
          orderBy('generatedAt', 'desc')
        );
        certificatesSnapshot = await getDocs(certificatesQuery);
      } catch (error) {
        // If index error, try fallback query without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.log('Index not available, using fallback query for certificates');
          const fallbackQuery = query(
            collection(db, COLLECTIONS.CERTIFICATES),
            where('coachingId', '==', coachingId),
            where('batchId', '==', batchId)
          );
          certificatesSnapshot = await getDocs(fallbackQuery);
        } else {
          throw error;
        }
      }
      console.log('Certificates snapshot size:', certificatesSnapshot.docs.length);
      const certs = [];
      for (const docSnap of certificatesSnapshot.docs) {
        const certData = { id: docSnap.id, ...docSnap.data() };
        // Get student details
        try {
          const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, certData.studentId));
          if (userDoc.exists()) {
            certData.studentData = userDoc.data();
          }
        } catch (err) {
          // Student not found
        }
        certs.push(certData);
      }
      // Sort by generatedAt if not already sorted
      certs.sort((a, b) => {
        const dateA = a.generatedAt?.toDate ? a.generatedAt.toDate() : new Date(a.generatedAt || 0);
        const dateB = b.generatedAt?.toDate ? b.generatedAt.toDate() : new Date(b.generatedAt || 0);
        return dateB - dateA;
      });
      setCertificates(certs);

      // Load holistic report cards
      try {
        const holisticCards = await getHolisticReportCards(coachingId, batchId);
        setHolisticReportCards(holisticCards);
        console.log('Holistic report cards loaded:', holisticCards.length);
      } catch (error) {
        console.error('Error loading holistic report cards:', error);
        setHolisticReportCards([]);
      }

      console.log('Data loaded successfully. Report cards:', cards.length, 'Holistic:', holisticReportCards.length, 'Certificates:', certs.length);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data: ' + (error.message || error.code || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async (batchId) => {
    if (!batchId) {
      setStudents([]);
      return;
    }
    
    try {
      setLoadingStudents(true);
      console.log('Loading students for batch:', batchId);
      const enrollmentsResult = await getBatchEnrollments(batchId);
      console.log('Enrollments result:', enrollmentsResult);
      
      if (enrollmentsResult.success && enrollmentsResult.data) {
        const batchStudents = [];
        
        // Extract emails from enrollments and query users
        for (const enrollment of enrollmentsResult.data) {
          const email = enrollment.email || enrollment.studentEmail || enrollment.userData?.email;
          if (email) {
            try {
              const userQuery = query(
                collection(db, COLLECTIONS.USERS),
                where('email', '==', email)
              );
              const userSnapshot = await getDocs(userQuery);
              
              if (!userSnapshot.empty) {
                const userDoc = userSnapshot.docs[0];
                const userData = userDoc.data();
                batchStudents.push({
                  id: userDoc.id,
                  ...userData,
                  batchId: batchId,
                  batchName: batches.find(b => b.id === batchId)?.name || ''
                });
              }
            } catch (err) {
              console.error('Error querying user by email:', err);
            }
          }
        }
        
        console.log('Mapped students:', batchStudents);
        setStudents(batchStudents);
        
        if (batchStudents.length === 0) {
          toast.error('No students found in this batch');
        } else {
          toast.success(`Loaded ${batchStudents.length} students`);
        }
      } else {
        console.error('Failed to load enrollments:', enrollmentsResult.error);
        toast.error(enrollmentsResult.error || 'No students found in this batch');
        setStudents([]);
      }
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students: ' + error.message);
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleBatchChangeForReportCard = (batchId) => {
    setSelectedBatchForReportCard(batchId);
    setReportCardForm({ ...reportCardForm, batchId: batchId || '', studentId: '' });
    setStudents([]); // Clear students immediately
    if (batchId) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        loadStudents(batchId);
      }, 100);
    }
  };

  const handleBatchChangeForCertificate = (batchId) => {
    setSelectedBatchForCertificate(batchId);
    setCertificateForm({ ...certificateForm, studentId: '' });
    setStudents([]); // Clear students immediately
    if (batchId) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        loadStudents(batchId);
      }, 100);
    }
  };

  const uploadGeneratedDocument = async (doc, fileName) => {
    try {
      const pdfBlob = doc.output('blob');
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
      const result = await uploadFile(file, `documents/${fileName}`);
      if (result.success) {
        return result.url;
      }
      return null;
    } catch (error) {
      console.error('Error uploading generated document:', error);
      return null;
    }
  };

  const handleGenerateReportCard = async () => {
    if (!reportCardForm.studentId || reportCardForm.subjects.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true); // Show loading state during upload/save
      // Calculate totals
      const totalMarks = reportCardForm.subjects.reduce((sum, sub) => sum + parseFloat(sub.maxMarks || 0), 0);
      const obtainedMarks = reportCardForm.subjects.reduce((sum, sub) => sum + parseFloat(sub.marks || 0), 0);
      const percentage = totalMarks > 0 ? (obtainedMarks / totalMarks) * 100 : 0;
      
      // Determine grade
      let grade = 'F';
      if (percentage >= 90) grade = 'A+';
      else if (percentage >= 80) grade = 'A';
      else if (percentage >= 70) grade = 'B+';
      else if (percentage >= 60) grade = 'B';
      else if (percentage >= 50) grade = 'C';
      else if (percentage >= 40) grade = 'D';

      const grades = {};
      reportCardForm.subjects.forEach(sub => {
        if (sub.name && sub.maxMarks) {
          const subPercentage = (parseFloat(sub.marks || 0) / parseFloat(sub.maxMarks)) * 100;
          let subGrade = 'F';
          if (subPercentage >= 90) subGrade = 'A+';
          else if (subPercentage >= 80) subGrade = 'A';
          else if (subPercentage >= 70) subGrade = 'B+';
          else if (subPercentage >= 60) subGrade = 'B';
          else if (subPercentage >= 50) subGrade = 'C';
          else if (subPercentage >= 40) subGrade = 'D';
          grades[sub.name] = subGrade;
        }
      });

      // Store subject details with marks
      const subjectDetails = reportCardForm.subjects
        .filter(s => s.name && s.maxMarks)
        .map(s => ({
          name: s.name,
          marks: parseFloat(s.marks || 0),
          maxMarks: parseFloat(s.maxMarks || 0)
        }));

      const student = students.find(s => s.id === reportCardForm.studentId);
      const batchName = batches.find(b => b.id === (selectedBatchForReportCard || selectedBatch))?.name || '';
      
      const cardData = {
        ...reportCardForm,
        coachingId: userProfile?.coachingId,
        batchId: selectedBatchForReportCard || selectedBatch,
        subjects: reportCardForm.subjects.map(s => s.name).filter(Boolean),
        subjectDetails,
        grades,
        totalMarks,
        obtainedMarks,
        percentage: parseFloat(percentage.toFixed(2)),
        grade
      };

      // Generate and upload PDF
      const docInstance = generateReportCardPDF({ 
        ...cardData, 
        studentData: student,
        batchName 
      });
      const fileName = `ReportCard-${student?.name || 'Student'}-${Date.now()}.pdf`;
      const reportCardUrl = await uploadGeneratedDocument(docInstance, fileName);
      
      if (reportCardUrl) {
        cardData.reportCardUrl = reportCardUrl;
      }

      let result;
      if (isEditing && selectedReportCard) {
        result = await updateReportCard(selectedReportCard.id, cardData);
      } else {
        result = await generateReportCard(cardData);
      }

      if (result.success) {
        toast.success(isEditing ? 'Report card updated successfully' : 'Report card generated successfully');
        setShowReportCardForm(false);
        setIsEditing(false);
        setSelectedReportCard(null);
        setSelectedBatchForReportCard(null);
        setReportCardForm({
          studentId: '',
          batchId: '',
          academicYear: new Date().getFullYear(),
          term: '',
          subjects: [{ name: '', marks: '', maxMarks: '' }],
          remarks: '',
          templateId: 'modern-clean',
          teacherName: '',
          principalName: '',
          rollNo: '',
          attendance: ''
        });
        setStudents([]);
        await loadData();
      } else {
        toast.error(result.error || 'Failed to save report card');
      }
    } catch (error) {
      console.error('Error saving report card:', error);
      toast.error('Failed to save report card');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCertificate = async () => {
    if (!certificateForm.studentId || !certificateForm.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const student = students.find(s => s.id === certificateForm.studentId);
      const certData = {
        ...certificateForm,
        coachingId: userProfile?.coachingId,
        batchId: selectedBatchForCertificate || selectedBatch,
        achievementDate: new Date(certificateForm.achievementDate)
      };

      // Generate and upload PDF
      const docInstance = generateCertificatePDF({ 
        ...certData, 
        studentData: student 
      });
      const fileName = `Certificate-${student?.name || 'Student'}-${Date.now()}.pdf`;
      const certificateUrl = await uploadGeneratedDocument(docInstance, fileName);
      
      if (certificateUrl) {
        certData.certificateUrl = certificateUrl;
      }

      let result;
      if (isEditing && selectedReportCard) { // Using selectedReportCard state for both edits
        result = await updateCertificate(selectedReportCard.id, certData);
      } else {
        result = await generateCertificate(certData);
      }

      if (result.success) {
        toast.success(isEditing ? 'Certificate updated successfully' : 'Certificate generated successfully');
        setShowCertificateForm(false);
        setIsEditing(false);
        setSelectedReportCard(null);
        setSelectedBatchForCertificate(null);
        setCertificateForm({
          studentId: '',
          certificateType: 'achievement',
          title: '',
          description: '',
          achievementDate: new Date().toISOString().split('T')[0],
          templateId: 'classic-gold',
          issuerName: '',
          issuerTitle: '',
          venue: ''
        });
        setStudents([]);
        await loadData();
      } else {
        toast.error(result.error || 'Failed to save certificate');
      }
    } catch (error) {
      console.error('Error saving certificate:', error);
      toast.error('Failed to save certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleEditReportCard = (card) => {
    setIsEditing(true);
    setSelectedReportCard(card);
    setSelectedBatchForReportCard(card.batchId);
    loadStudents(card.batchId);
    setReportCardForm({
      studentId: card.studentId,
      batchId: card.batchId,
      academicYear: card.academicYear,
      term: card.term,
      subjects: card.subjectDetails.map(s => ({ ...s })),
      remarks: card.remarks,
      templateId: card.templateId || 'modern-clean',
      teacherName: card.teacherName || '',
      principalName: card.principalName || '',
      rollNo: card.rollNo || '',
      attendance: card.attendance || ''
    });
    setShowReportCardForm(true);
  };

  const handleEditCertificate = (cert) => {
    setIsEditing(true);
    setSelectedReportCard(cert); // Reuse state
    setSelectedBatchForCertificate(cert.batchId);
    loadStudents(cert.batchId);
    setCertificateForm({
      studentId: cert.studentId,
      certificateType: cert.certificateType,
      title: cert.title,
      description: cert.description,
      achievementDate: cert.achievementDate?.toDate ? cert.achievementDate.toDate().toISOString().split('T')[0] : new Date(cert.achievementDate).toISOString().split('T')[0],
      templateId: cert.templateId || 'classic-gold',
      issuerName: cert.issuerName || '',
      issuerTitle: cert.issuerTitle || '',
      venue: cert.venue || ''
    });
    setShowCertificateForm(true);
  };

  const handleDelete = async () => {
    const { id, type } = deleteConfirm;
    try {
      let result;
      if (type === 'report-card') {
        result = await deleteReportCard(id);
      } else {
        result = await deleteCertificate(id);
      }

      if (result.success) {
        toast.success(`${type === 'report-card' ? 'Report card' : 'Certificate'} deleted successfully`);
        await loadData();
      } else {
        toast.error(result.error || 'Deletion failed');
      }
    } catch (error) {
      toast.error('Deletion failed');
    } finally {
      setDeleteConfirm({ show: false, id: null, type: null });
    }
  };

  const handleDownloadReportCard = (card) => {
    // Ensure we have the batch name for the PDF
    const batchName = batches.find(b => b.id === card.batchId)?.name || '';
    const doc = generateReportCardPDF({ ...card, batchName });
    doc.save(`ReportCard-${card.studentData?.name || 'Student'}.pdf`);
  };

  const handleDownloadCertificate = (certificate) => {
    const doc = generateCertificatePDF(certificate);
    doc.save(`Certificate-${certificate.studentData?.name || 'Student'}.pdf`);
  };

  const handleDownloadHolisticReport = (card) => {
    try {
      const doc = new jsPDF();
      const student = card.studentData;
      const instituteInfo = userProfile?.reportCardInstituteDefaults || {};

      // Color theme - using indigo as default
      const primaryColor = [79, 70, 229];
      const primaryColorHex = '#4F46E5';
      const secondaryColor = '#EC4899';
      const headerTextColor = '#FFFFFF';
      const tableHeaderColor = [224, 231, 255];
      const grayText = [107, 114, 128];

      // Draw Header Background
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, 210, 40, 'F');
      
      // Institute Name
      doc.setFontSize(20);
      doc.setTextColor(headerTextColor);
      doc.setFont('helvetica', 'bold');
      doc.text(userProfile?.coachingName || 'Institute Name', 105, 15, { align: 'center' });
      
      // Institute Details
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      if (instituteInfo.address) {
        doc.text(instituteInfo.address, 105, 23, { align: 'center' });
      }
      let contactLine = [];
      if (instituteInfo.institutePhone) contactLine.push(`Phone: ${instituteInfo.institutePhone}`);
      if (instituteInfo.instituteEmail) contactLine.push(`Email: ${instituteInfo.instituteEmail}`);
      if (contactLine.length > 0) {
        doc.text(contactLine.join(' | '), 105, 29, { align: 'center' });
      }
      if (instituteInfo.affiliationNumber) {
        doc.text(`Affiliation No: ${instituteInfo.affiliationNumber}`, 105, 35, { align: 'center' });
      }

      let y = 50;
      const leftCol = 14;
      const midCol = 105;
      const lineHeight = 6;

      // Helper function for section headers
      const addSectionHeader = (title, yPos) => {
        doc.setFontSize(12);
        doc.setTextColor(primaryColorHex);
        doc.setFont('helvetica', 'bold');
        doc.text(title, leftCol, yPos);
        doc.setDrawColor(secondaryColor);
        doc.line(leftCol, yPos + 2, 196, yPos + 2);
        return yPos + 10;
      };

      // Helper function for label-value pairs
      const addLabelValue = (label, value, x, yPos, labelWidth = 35) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(`${label}:`, x, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(value || '', x + labelWidth, yPos);
      };

      // 1. Student Details
      y = addSectionHeader('1. Student Details', y);
      doc.setTextColor(0, 0, 0);
      
      addLabelValue('Name', student?.name || student?.displayName || '', leftCol, y, 15);
      addLabelValue('Class', card.class || '', midCol, y, 15);
      y += lineHeight;
      
      addLabelValue('Section', card.section || '', leftCol, y, 18);
      addLabelValue('Roll Number', card.rollNumber || '', midCol, y, 28);
      y += lineHeight;
      
      addLabelValue('Date of Birth', card.dateOfBirth || '', leftCol, y, 30);
      addLabelValue('Academic Session', card.academicSession || '', midCol, y, 38);
      y += 12;

      // 2. Academic Performance (with table)
      y = addSectionHeader('2. Academic Performance', y);
      
      if (card.reportType === 'exam') {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Exam: ${card.examName || ''}`, leftCol, y);
        y += 6;
        
        autoTable(doc, { 
          startY: y, 
          head: [['Subject', 'Marks Obtained', 'Maximum Marks', 'Grade', 'Remarks']],
          body: (card.subjects || []).map(subject => [
            subject.name || '',
            subject.marks || '',
            subject.maxMarks || '',
            subject.grade || '',
            subject.remarks || '',
          ]),
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: tableHeaderColor, textColor: primaryColor, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [249, 250, 251] },
        });
      } else if (card.finalReport) {
        const examHeaders = ['Subject', ...(card.finalReport.exams || []).map(e => `${e.name} (${e.maxMarks})`), 'Total Marks'];
        autoTable(doc, { 
          startY: y, 
          head: [examHeaders],
          body: (card.finalReport.subjects || []).map(subject => [
            subject.name || '',
            ...(subject.marks || []).map(m => m || ''),
            (subject.marks || []).reduce((a, b) => Number(a) + Number(b), 0),
          ]),
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: tableHeaderColor, textColor: primaryColor, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [249, 250, 251] },
        });
      }
      y = doc.lastAutoTable.finalY + 12;

      // 3. Overall Academic Summary
      y = addSectionHeader('3. Overall Academic Summary', y);
      doc.setTextColor(0, 0, 0);
      
      addLabelValue('Total Marks Obtained', String(card.summary?.totalMarksObtained || '0'), leftCol, y, 45);
      addLabelValue('Total Maximum Marks', String(card.summary?.totalMaximumMarks || '0'), midCol, y, 45);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColorHex);
      doc.text(`Percentage: ${card.summary?.percentage || '0'}%`, 160, y);
      y += lineHeight;
      
      doc.setTextColor(0, 0, 0);
      addLabelValue('Overall Grade', card.summary?.overallGrade || '', leftCol, y, 32);
      addLabelValue('Rank', card.summary?.rank || '', midCol, y, 15);
      y += 12;

      // 4. Co-Scholastic Areas
      if (card.coScholastic) {
        y = addSectionHeader('4. Co-Scholastic Areas', y);
        doc.setTextColor(0, 0, 0);
        
        addLabelValue('Discipline', card.coScholastic.discipline || '', leftCol, y, 25);
        addLabelValue('Communication Skills', card.coScholastic.communicationSkills || '', midCol - 20, y, 45);
        addLabelValue('Participation', card.coScholastic.participation || '', 155, y, 30);
        y += lineHeight;
        
        addLabelValue('Teamwork', card.coScholastic.teamwork || '', leftCol, y, 25);
        addLabelValue('Leadership', card.coScholastic.leadership || '', midCol - 20, y, 28);
        addLabelValue('Creativity', card.coScholastic.creativity || '', 155, y, 25);
        y += 12;
      }

      // 5. Skills
      if (card.skills) {
        y = addSectionHeader('5. Skills', y);
        doc.setTextColor(0, 0, 0);
        
        addLabelValue('Critical Thinking', card.skills.criticalThinking || '', leftCol, y, 38);
        addLabelValue('Problem Solving', card.skills.problemSolving || '', midCol - 20, y, 38);
        addLabelValue('Analytical Skills', card.skills.analyticalSkills || '', 155, y, 38);
        y += lineHeight;
        
        addLabelValue('Collaboration', card.skills.collaboration || '', leftCol, y, 30);
        addLabelValue('Initiative', card.skills.initiative || '', midCol - 20, y, 22);
        y += 12;
      }

      // 6. Attendance
      if (card.attendance) {
        y = addSectionHeader('6. Attendance', y);
        doc.setTextColor(0, 0, 0);
        
        addLabelValue('Total Working Days', card.attendance.totalWorkingDays || '', leftCol, y, 42);
        addLabelValue('Days Present', card.attendance.daysPresent || '', midCol, y, 30);
        addLabelValue('Percentage', `${card.attendance.percentage || '0'}%`, 155, y, 28);
        y += 20;
      }

      // Footer - Teacher and Principal Remarks
      if (card.footer) {
        doc.setDrawColor(200, 200, 200);
        doc.line(leftCol, y - 8, 196, y - 8);

        // Teacher's Remarks (left side)
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text("Teacher's Remarks", leftCol, y);
        y += 5;
        
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...grayText);
        doc.setFontSize(9);
        const teacherRemarks = card.footer.teacherRemarks || '';
        const splitTeacherRemarks = doc.splitTextToSize(teacherRemarks, 80);
        doc.text(splitTeacherRemarks, leftCol, y);
        
        // Principal's Remarks (right side)
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.text("Principal's Remarks", 196, y - 5, { align: 'right' });
        
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...grayText);
        doc.setFontSize(9);
        const principalRemarks = card.footer.principalRemarks || '';
        const splitPrincipalRemarks = doc.splitTextToSize(principalRemarks, 80);
        doc.text(splitPrincipalRemarks, 196, y, { align: 'right' });
        
        y += Math.max(splitTeacherRemarks.length, splitPrincipalRemarks.length) * 4 + 10;

        // Signatures
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.text(card.footer.teacherName || '', leftCol, y);
        doc.text(card.footer.principalName || '', 196, y, { align: 'right' });
        y += 4;
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...grayText);
        doc.setFontSize(8);
        doc.text("(Teacher's Signature)", leftCol, y);
        doc.text("(Principal's Signature)", 196, y, { align: 'right' });
      }

      doc.save(`${student?.name || 'student'}_holistic_report.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const getInstituteHeaderMeta = () => {
    const d = userProfile?.reportCardInstituteDefaults || {};
    return {
      address: (d.address || '').trim(),
      affiliationNumber: (d.affiliationNumber || '').trim(),
      instituteEmail: (d.instituteEmail || '').trim(),
      institutePhone: (d.institutePhone || '').trim()
    };
  };

  const InstituteHeaderPreview = ({ templateId }) => {
    const meta = getInstituteHeaderMeta();
    const hasMeta = !!(meta.address || meta.affiliationNumber || meta.instituteEmail || meta.institutePhone);
    if (templateId === 'modern-clean') {
      if (!hasMeta) return null;
      return (
        <div className="text-blue-100 text-xs mt-3 space-y-1 max-w-lg mx-auto leading-relaxed">
          {meta.address ? <p className="whitespace-pre-line">{meta.address}</p> : null}
          {meta.affiliationNumber ? <p>Affiliation No.: {meta.affiliationNumber}</p> : null}
          {(meta.instituteEmail || meta.institutePhone) ? (
            <p>{[meta.instituteEmail, meta.institutePhone].filter(Boolean).join(' · ')}</p>
          ) : null}
        </div>
      );
    }
    if (!hasMeta) {
      return <p className="text-slate-500 italic text-sm">Excellence in Education</p>;
    }
    return (
      <div className="text-slate-600 text-xs mt-2 space-y-1 max-w-lg mx-auto leading-relaxed">
        {meta.address ? <p className="whitespace-pre-line">{meta.address}</p> : null}
        {meta.affiliationNumber ? <p>Affiliation No.: {meta.affiliationNumber}</p> : null}
        {(meta.instituteEmail || meta.institutePhone) ? (
          <p>{[meta.instituteEmail, meta.institutePhone].filter(Boolean).join(' · ')}</p>
        ) : null}
      </div>
    );
  };

  const ReportCardPreview = ({ data, templateId }) => {
    const student = students.find(s => s.id === data.studentId) || { name: 'Student Name' };
    const batchName = batches.find(b => b.id === (selectedBatchForReportCard || selectedBatch))?.name || 'Batch Name';
    
    return (
      <div className={`w-full bg-white border shadow-inner p-8 text-slate-800 font-sans min-h-[600px] relative overflow-hidden ${templateId === 'academic-classic' ? 'border-4 border-double border-slate-800' : ''}`}>
        {/* Header */}
        {templateId === 'modern-clean' ? (
          <div className="bg-blue-600 -m-8 mb-8 p-8 text-center text-white">
            <h2 className="text-2xl font-bold uppercase tracking-wider">{userProfile?.coachingName || 'Institute Name'}</h2>
            <p className="text-blue-100 text-sm mt-1">Official Progress Report</p>
            <InstituteHeaderPreview templateId="modern-clean" />
          </div>
        ) : (
          <div className="text-center mb-8 pb-4 border-b border-slate-200">
            <h2 className="text-2xl font-serif font-bold text-slate-900 uppercase tracking-wide">{userProfile?.coachingName || 'Institute Name'}</h2>
            <InstituteHeaderPreview templateId={templateId} />
          </div>
        )}

        {/* Student Info */}
        <div className="grid grid-cols-2 gap-y-3 gap-x-8 mb-8 text-sm">
          <div className="flex justify-between border-b border-slate-100 pb-1">
            <span className="font-bold text-slate-500 uppercase text-[10px]">Student Name:</span>
            <span className="font-semibold">{student.name || student.displayName}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 pb-1">
            <span className="font-bold text-slate-500 uppercase text-[10px]">Roll No:</span>
            <span className="font-semibold">{data.rollNo || 'N/A'}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 pb-1">
            <span className="font-bold text-slate-500 uppercase text-[10px]">Academic Year:</span>
            <span className="font-semibold">{data.academicYear}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 pb-1">
            <span className="font-bold text-slate-500 uppercase text-[10px]">Batch:</span>
            <span className="font-semibold">{batchName}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 pb-1">
            <span className="font-bold text-slate-500 uppercase text-[10px]">Term:</span>
            <span className="font-semibold">{data.term || 'Annual'}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 pb-1">
            <span className="font-bold text-slate-500 uppercase text-[10px]">Attendance:</span>
            <span className="font-semibold">{data.attendance || 'N/A'}</span>
          </div>
        </div>

        {/* Marks Table */}
        <table className="w-full text-sm mb-8 border-collapse">
          <thead>
            <tr className={templateId === 'modern-clean' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-white'}>
              <th className="p-2 text-left uppercase text-[10px]">Subject</th>
              <th className="p-2 text-center uppercase text-[10px]">Marks</th>
              <th className="p-2 text-center uppercase text-[10px]">Max</th>
              <th className="p-2 text-center uppercase text-[10px]">Grade</th>
            </tr>
          </thead>
          <tbody>
            {data.subjects.map((sub, idx) => (
              <tr key={idx} className={idx % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
                <td className="p-2 border-b border-slate-100 font-medium">{sub.name || '---'}</td>
                <td className="p-2 border-b border-slate-100 text-center">{sub.marks || '0'}</td>
                <td className="p-2 border-b border-slate-100 text-center">{sub.maxMarks || '0'}</td>
                <td className="p-2 border-b border-slate-100 text-center font-bold text-blue-600">
                  {sub.name && sub.maxMarks ? (
                    (() => {
                      const p = (parseFloat(sub.marks || 0) / parseFloat(sub.maxMarks)) * 100;
                      if (p >= 90) return 'A+'; if (p >= 80) return 'A'; if (p >= 70) return 'B+';
                      if (p >= 60) return 'B'; if (p >= 50) return 'C'; if (p >= 40) return 'D';
                      return 'F';
                    })()
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summary and Remarks */}
        <div className="flex gap-8 mb-12">
          <div className="flex-1">
            <h4 className="font-bold text-slate-500 uppercase text-[10px] mb-2">Remarks</h4>
            <div className="p-3 bg-slate-50 rounded border border-slate-100 italic text-sm text-slate-600 min-h-[60px]">
              {data.remarks || 'No specific remarks.'}
            </div>
          </div>
          <div className="w-48">
            <div className={`p-4 border-2 rounded-lg ${templateId === 'modern-clean' ? 'border-blue-600' : 'border-slate-800'}`}>
              <h4 className="text-center font-bold text-[10px] uppercase mb-2 border-b pb-1">Summary</h4>
              {(() => {
                const total = data.subjects.reduce((s, b) => s + parseFloat(b.maxMarks || 0), 0);
                const obt = data.subjects.reduce((s, b) => s + parseFloat(b.marks || 0), 0);
                const perc = total > 0 ? ((obt / total) * 100).toFixed(1) : 0;
                return (
                  <div className="space-y-1 text-[11px]">
                    <div className="flex justify-between"><span>Total:</span><span className="font-bold">{obt}</span></div>
                    <div className="flex justify-between"><span>Out of:</span><span>{total}</span></div>
                    <div className="flex justify-between text-blue-600"><span>Perc:</span><span className="font-bold">{perc}%</span></div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Signatures */}
        <div className="flex justify-between items-end px-4 mt-auto absolute bottom-8 left-0 right-0">
          <div className="text-center w-32">
            <div className="border-t border-slate-300 pt-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Class Teacher</p>
              <p className="text-xs font-semibold">{data.teacherName || '---'}</p>
            </div>
          </div>
          <div className="text-center w-32">
            <div className="border-t border-slate-300 pt-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Principal</p>
              <p className="text-xs font-semibold">{data.principalName || '---'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CertificatePreview = ({ data, templateId }) => {
    const student = students.find(s => s.id === data.studentId) || { name: 'Student Name' };
    
    return (
      <div className={`w-full bg-white border shadow-inner p-12 text-slate-800 font-sans aspect-[1.4/1] relative overflow-hidden flex flex-col items-center justify-center`}>
        {/* Border */}
        {templateId === 'classic-gold' && (
          <>
            <div className="absolute inset-2 border-[6px] border-[#d4af37]"></div>
            <div className="absolute inset-4 border border-[#d4af37]"></div>
          </>
        )}
        {templateId === 'modern-blue' && (
          <div className="absolute left-0 top-0 bottom-0 w-6 bg-blue-600"></div>
        )}

        <div className="z-10 text-center max-w-full px-8">
          <h2 className="text-2xl font-bold uppercase text-slate-900 tracking-widest mb-1">{userProfile?.coachingName || 'INSTITUTE NAME'}</h2>
          <p className="text-[10px] tracking-[0.2em] text-slate-500 mb-12">OFFICIAL RECOGNITION OF EXCELLENCE</p>
          
          <h1 className={`text-5xl font-serif font-bold tracking-tight mb-2 ${templateId === 'classic-gold' ? 'text-[#b8860b]' : 'text-blue-600'}`}>CERTIFICATE</h1>
          <p className="text-lg font-medium text-slate-600 mb-10">OF {data.certificateType?.toUpperCase()}</p>
          
          <p className="text-sm text-slate-500 italic mb-4">This is proudly presented to</p>
          <h3 className="text-4xl font-serif font-bold text-slate-900 uppercase border-b-2 border-slate-100 pb-2 mb-8">{student.name || student.displayName}</h3>
          
          <p className="text-sm text-slate-600 leading-relaxed max-w-lg mx-auto">
            {data.description || `for outstanding performance and dedicated commitment shown in ${data.title || 'the designated course of study'}.`}
          </p>
          
          {data.venue && <p className="text-xs font-semibold mt-4 text-slate-500">Venue: {data.venue}</p>}

          <div className="flex justify-between w-full mt-16 px-4">
            <div className="text-center w-40 border-t border-slate-200 pt-2">
              <p className="text-xs font-bold text-slate-900">{data.issuerName || 'Director'}</p>
              <p className="text-[10px] text-slate-400 uppercase">{data.issuerTitle || 'Authorized Signatory'}</p>
            </div>
            <div className="text-center w-40 border-t border-slate-200 pt-2">
              <p className="text-xs font-bold text-slate-900">{new Date(data.achievementDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              <p className="text-[10px] text-slate-400 uppercase">Date</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Header for non–modern-clean PDF templates; returns Y below the header block (before divider)
  const addSchoolBranding = (doc, pageWidth, yStart = 20) => {
    const coachingName = userProfile?.coachingName || 'Institute Name';
    const meta = getInstituteHeaderMeta();
    const hasMeta = !!(meta.address || meta.affiliationNumber || meta.instituteEmail || meta.institutePhone);
    const maxW = pageWidth - 40;

    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(coachingName, pageWidth / 2, yStart, { align: 'center' });

    let y = yStart + 8;
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    if (hasMeta) {
      if (meta.address) {
        doc.splitTextToSize(meta.address, maxW).forEach((line) => {
          doc.text(line, pageWidth / 2, y, { align: 'center' });
          y += 4.5;
        });
      }
      if (meta.affiliationNumber) {
        doc.text(`Affiliation No.: ${meta.affiliationNumber}`, pageWidth / 2, y, { align: 'center' });
        y += 4.5;
      }
      const contactLine = [meta.instituteEmail, meta.institutePhone].filter(Boolean).join(' · ');
      if (contactLine) {
        doc.splitTextToSize(contactLine, maxW).forEach((line) => {
          doc.text(line, pageWidth / 2, y, { align: 'center' });
          y += 4.5;
        });
      }
    } else {
      doc.text('Excellence in Education', pageWidth / 2, y, { align: 'center' });
      y += 6;
    }
    return y;
  };

  const generateReportCardPDF = (reportCard) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const templateId = reportCard.templateId || 'modern-clean';
    const meta = getInstituteHeaderMeta();
    const hasInstituteMeta = !!(meta.address || meta.affiliationNumber || meta.instituteEmail || meta.institutePhone);
    
    if (templateId === 'academic-classic') {
      // Classic Border
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(1);
      doc.rect(5, 5, pageWidth - 10, doc.internal.pageSize.getHeight() - 10, 'S');
    }

    let yPos;
    if (templateId === 'modern-clean') {
      const marginX = 25;
      const maxW = pageWidth - 2 * marginX;
      const lineHeight = 4.2;
      const metaChunks = [];
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      if (hasInstituteMeta) {
        if (meta.address) metaChunks.push(...doc.splitTextToSize(meta.address, maxW));
        if (meta.affiliationNumber) metaChunks.push(`Affiliation No.: ${meta.affiliationNumber}`);
        const contactLine = [meta.instituteEmail, meta.institutePhone].filter(Boolean).join(' · ');
        if (contactLine) metaChunks.push(...doc.splitTextToSize(contactLine, maxW));
      }
      const headerBottom = hasInstituteMeta
        ? 29 + metaChunks.length * lineHeight + 8
        : 45;

      doc.setFillColor(59, 130, 246);
      doc.rect(0, 0, pageWidth, headerBottom, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.text(userProfile?.coachingName || 'Institute', pageWidth / 2, 16, { align: 'center' });
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text('Official Progress Report', pageWidth / 2, 24, { align: 'center' });
      if (hasInstituteMeta) {
        doc.setFontSize(8);
        doc.setTextColor(220, 235, 255);
        let yMeta = 29;
        metaChunks.forEach((line) => {
          doc.text(line, pageWidth / 2, yMeta, { align: 'center' });
          yMeta += lineHeight;
        });
      }
      yPos = headerBottom + 12;
    } else {
      const brandingBottom = addSchoolBranding(doc, pageWidth, 25);
      doc.setDrawColor(226, 232, 240);
      doc.line(20, brandingBottom + 4, pageWidth - 20, brandingBottom + 4);
      yPos = brandingBottom + 18;
    }
    
    // Student details
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(11);
    
    const drawDetail = (label, value, x, y) => {
      doc.setFont(undefined, 'bold');
      doc.text(`${label}:`, x, y);
      doc.setFont(undefined, 'normal');
      doc.text(value || 'N/A', x + 35, y);
    };

    drawDetail('Student Name', reportCard.studentData?.name || reportCard.studentData?.displayName || 'Student', 20, yPos);
    drawDetail('Roll No', reportCard.rollNo || 'N/A', pageWidth / 2 + 10, yPos);
    yPos += 10;
    drawDetail('Academic Year', (reportCard.academicYear || '').toString(), 20, yPos);
    drawDetail('Batch', reportCard.batchName || 'N/A', pageWidth / 2 + 10, yPos);
    yPos += 10;
    drawDetail('Term', reportCard.term || 'Annual', 20, yPos);
    drawDetail('Attendance', reportCard.attendance || 'N/A', pageWidth / 2 + 10, yPos);
    yPos += 20;
    
    // Subjects table header
    doc.setFillColor(templateId === 'modern-clean' ? 59 : 30, templateId === 'modern-clean' ? 130 : 41, templateId === 'modern-clean' ? 246 : 59);
    doc.rect(20, yPos, pageWidth - 40, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    
    const col1X = 25;
    const col2X = pageWidth - 110;
    const col3X = pageWidth - 70;
    const col4X = pageWidth - 30;
    
    doc.text('SUBJECT', col1X, yPos + 8);
    doc.text('MARKS', col2X, yPos + 8, { align: 'center' });
    doc.text('MAX', col3X, yPos + 8, { align: 'center' });
    doc.text('GRADE', col4X, yPos + 8, { align: 'center' });
    yPos += 12;
    
    // Subject rows
    doc.setTextColor(30, 41, 59);
    doc.setFont(undefined, 'normal');
    const subjectsToDisplay = reportCard.subjectDetails || [];
    
    subjectsToDisplay.forEach((subject, idx) => {
      if (idx % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(20, yPos, pageWidth - 40, 10, 'F');
      }
      doc.setDrawColor(226, 232, 240);
      doc.line(20, yPos + 10, pageWidth - 20, yPos + 10);
      
      doc.text(subject.name || '---', col1X, yPos + 7);
      doc.text((subject.marks || 0).toString(), col2X, yPos + 7, { align: 'center' });
      doc.text((subject.maxMarks || 0).toString(), col3X, yPos + 7, { align: 'center' });
      doc.text(reportCard.grades?.[subject.name] || '-', col4X, yPos + 7, { align: 'center' });
      yPos += 10;
    });
    
    yPos += 15;
    
    // Summary Box
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.rect(pageWidth - 90, yPos, 70, 45);
    
    doc.setFont(undefined, 'bold');
    doc.text('RESULT SUMMARY', pageWidth - 85, yPos + 8);
    doc.setFont(undefined, 'normal');
    doc.line(pageWidth - 90, yPos + 12, pageWidth - 20, yPos + 12);
    
    const drawSummaryLine = (label, value, y) => {
      doc.text(label, pageWidth - 85, y);
      doc.text(value, pageWidth - 25, y, { align: 'right' });
    };

    drawSummaryLine('Total Marks:', (reportCard.obtainedMarks || 0).toString(), yPos + 20);
    drawSummaryLine('Out of:', (reportCard.totalMarks || 0).toString(), yPos + 28);
    drawSummaryLine('Percentage:', `${reportCard.percentage || 0}%`, yPos + 36);
    doc.setFont(undefined, 'bold');
    drawSummaryLine('Final Grade:', reportCard.grade || '-', yPos + 44);
    
    // Remarks
    if (reportCard.remarks) {
      doc.setFont(undefined, 'bold');
      doc.text('REMARKS:', 20, yPos + 8);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      doc.text(reportCard.remarks, 20, yPos + 16, { maxWidth: 90 });
    }
    
    // Signatures
    const sigY = doc.internal.pageSize.getHeight() - 40;
    doc.setFontSize(10);
    doc.line(20, sigY, 70, sigY);
    doc.text('Class Teacher', 45, sigY + 7, { align: 'center' });
    if (reportCard.teacherName) doc.text(reportCard.teacherName, 45, sigY - 5, { align: 'center' });

    doc.line(pageWidth - 70, sigY, pageWidth - 20, sigY);
    doc.text('Principal', pageWidth - 45, sigY + 7, { align: 'center' });
    if (reportCard.principalName) doc.text(reportCard.principalName, pageWidth - 45, sigY - 5, { align: 'center' });
    
    return doc;
  };

  const generateCertificatePDF = (certificate) => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const templateId = certificate.templateId || 'classic-gold';
    
    // Border
    if (templateId === 'classic-gold') {
      doc.setDrawColor(212, 175, 55); // Gold
      doc.setLineWidth(5);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20, 'S');
      doc.setLineWidth(1);
      doc.rect(13, 13, pageWidth - 26, pageHeight - 26, 'S');
    } else if (templateId === 'modern-blue') {
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 0, 20, pageHeight, 'F');
      doc.rect(pageWidth - 20, 0, 20, pageHeight, 'F');
    }

    // Logo Placeholder / Institute Name
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(28);
    doc.setFont(undefined, 'bold');
    doc.text(userProfile?.coachingName || 'INSTITUTE NAME', pageWidth / 2, 40, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text('OFFICIAL RECOGNITION OF EXCELLENCE', pageWidth / 2, 50, { align: 'center' });
    
    // Title
    doc.setFontSize(40);
    doc.setTextColor(templateId === 'classic-gold' ? 184 : 59, templateId === 'classic-gold' ? 134 : 130, templateId === 'classic-gold' ? 11 : 246);
    doc.text('CERTIFICATE', pageWidth / 2, 80, { align: 'center' });
    doc.setFontSize(20);
    doc.text('OF ' + certificate.certificateType.toUpperCase(), pageWidth / 2, 92, { align: 'center' });

    // Body
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(16);
    doc.text('This is proudly presented to', pageWidth / 2, 110, { align: 'center' });
    
    doc.setFontSize(32);
    doc.setFont(undefined, 'bold');
    const studentName = (certificate.studentData?.name || certificate.studentData?.displayName || 'Student').toUpperCase();
    doc.text(studentName, pageWidth / 2, 125, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'normal');
    const desc = certificate.description || `for outstanding performance in ${certificate.title}`;
    const descLines = doc.splitTextToSize(desc, pageWidth - 100);
    doc.text(descLines, pageWidth / 2, 140, { align: 'center' });

    if (certificate.venue) {
      doc.setFontSize(12);
      doc.text(`Venue: ${certificate.venue}`, pageWidth / 2, 155, { align: 'center' });
    }

    // Signatures
    const sigY = pageHeight - 40;
    doc.setDrawColor(200, 200, 200);
    
    doc.line(50, sigY, 120, sigY);
    doc.setFontSize(12);
    doc.text(certificate.issuerName || 'Director', 85, sigY + 8, { align: 'center' });
    doc.setFontSize(10);
    doc.text(certificate.issuerTitle || 'Authorized Signatory', 85, sigY + 15, { align: 'center' });

    doc.line(pageWidth - 120, sigY, pageWidth - 50, sigY);
    const date = certificate.achievementDate?.toDate ? certificate.achievementDate.toDate() : new Date(certificate.achievementDate);
    doc.text(date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }), pageWidth - 85, sigY + 8, { align: 'center' });
    doc.text('Date of Achievement', pageWidth - 85, sigY + 15, { align: 'center' });

    return doc;
  };

  const addSubject = () => {
    setReportCardForm({
      ...reportCardForm,
      subjects: [...reportCardForm.subjects, { name: '', marks: '', maxMarks: '' }]
    });
  };

  const removeSubject = (index) => {
    setReportCardForm({
      ...reportCardForm,
      subjects: reportCardForm.subjects.filter((_, i) => i !== index)
    });
  };

  const updateSubject = (index, field, value) => {
    const newSubjects = [...reportCardForm.subjects];
    newSubjects[index][field] = value;
    setReportCardForm({ ...reportCardForm, subjects: newSubjects });
  };

  const filteredReportCards = reportCards.filter(card => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      card.studentData?.name?.toLowerCase().includes(searchLower) ||
      card.studentData?.displayName?.toLowerCase().includes(searchLower) ||
      card.studentData?.email?.toLowerCase().includes(searchLower) ||
      card.studentId?.toLowerCase().includes(searchLower) ||
      card.academicYear?.toString().includes(searchLower) ||
      card.term?.toLowerCase().includes(searchLower)
    );
  });

  const filteredCertificates = certificates.filter(cert =>
    cert.studentData?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Report Cards & Certificates
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Generate and manage student report cards and certificates
        </p>
      </div>

      {/* Batch Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Batch
        </label>
        <select
          value={selectedBatch || ''}
          onChange={(e) => {
            const batchId = e.target.value;
            setSelectedBatch(batchId || null);
            setReportCards([]);
            setHolisticReportCards([]);
            setCertificates([]);
            setStudents([]);
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select a batch</option>
          {batches.map(batch => (
            <option key={batch.id} value={batch.id}>
              {batch.name}
            </option>
          ))}
        </select>
        {batches.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            No batches available. Please create a batch first.
          </p>
        )}
      </div>

      {/* Show message if no batch selected */}
      {!selectedBatch && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <FileText className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Select a Batch to View Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Please select a batch from the dropdown above to view and manage report cards and certificates.
          </p>
        </div>
      )}

      {/* Tabs - Only show when batch is selected */}
      {selectedBatch && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            {[
              { id: 'report-cards', label: 'Report Cards', icon: FileText },
              { id: 'holistic-reports', label: 'Holistic Reports', icon: BookOpen },
              { id: 'certificates', label: 'Certificates', icon: Award }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-4 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon size={18} className="mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Search */}
          <div className="flex items-center justify-between mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by student name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              {activeTab === 'report-cards' && (
                <>
                  <button
                    onClick={() => setShowReportCardForm(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={18} className="mr-2" />
                    Generate Report Card
                  </button>
                  <button
                    onClick={() => {
                      setEditingHolisticReport(null);
                      setShowHolisticReportCardForm(true);
                    }}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors ml-4"
                  >
                    <Plus size={18} className="mr-2" />
                    Generate Holistic Report
                  </button>
                </>
              )}
              {activeTab === 'certificates' && (
                <button
                  onClick={() => setShowCertificateForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={18} className="mr-2" />
                  Generate Certificate
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
            </div>
          ) : (
            <>
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-4 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                  Debug: Total cards: {reportCards.length}, Filtered: {filteredReportCards.length}, Search: "{searchTerm}"
                </div>
              )}
              
              {activeTab === 'report-cards' && (
                <>
                <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-slate-50 dark:bg-slate-900/30">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Institute details (default on all report cards)
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 mb-4">
                    Address, affiliation, email, and phone appear below your institute name on every report card preview and PDF. Save here before generating or downloading so the PDF includes the latest details.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Institute address
                      </label>
                      <textarea
                        value={instituteDefaultsForm.address}
                        onChange={(e) => setInstituteDefaultsForm({ ...instituteDefaultsForm, address: e.target.value })}
                        rows={3}
                        placeholder="Street, city, state, PIN"
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Affiliation number (optional)
                      </label>
                      <input
                        type="text"
                        value={instituteDefaultsForm.affiliationNumber}
                        onChange={(e) => setInstituteDefaultsForm({ ...instituteDefaultsForm, affiliationNumber: e.target.value })}
                        placeholder="e.g. board / affiliation ID"
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Institute contact number
                      </label>
                      <input
                        type="text"
                        value={instituteDefaultsForm.institutePhone}
                        onChange={(e) => setInstituteDefaultsForm({ ...instituteDefaultsForm, institutePhone: e.target.value })}
                        placeholder="Phone"
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Institute email
                      </label>
                      <input
                        type="email"
                        value={instituteDefaultsForm.instituteEmail}
                        onChange={(e) => setInstituteDefaultsForm({ ...instituteDefaultsForm, instituteEmail: e.target.value })}
                        placeholder="contact@institute.edu"
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={handleSaveInstituteReportDefaults}
                      disabled={savingInstituteDefaults}
                      className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {savingInstituteDefaults ? 'Saving…' : 'Save institute details'}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredReportCards.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
                      {reportCards.length === 0 
                        ? 'No report cards found. Generate your first report card above.' 
                        : `No report cards match your search. Total cards: ${reportCards.length}`}
                    </div>
                  ) : (
                    filteredReportCards.map(card => (
                      <div key={card.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <GraduationCap className="text-blue-600" size={24} />
                            <div>
                              <h3 className="font-semibold text-gray-900 dark:text-white">
                                {card.studentData?.name || card.studentData?.displayName || card.studentData?.fullName || 'Student'}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {card.academicYear} - {card.term || 'Annual'}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Percentage:</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{card.percentage}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Grade:</span>
                            <span className="font-semibold text-blue-600 dark:text-blue-400">{card.grade}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Marks:</span>
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {card.obtainedMarks} / {card.totalMarks}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleDownloadReportCard(card)}
                            className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Download size={18} className="mr-2" />
                            Download
                          </button>
                          <button
                            onClick={() => handleEditReportCard(card)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                            title="Edit Report Card"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ show: true, id: card.id, type: 'report-card' })}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                            title="Delete Report Card"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                </>
              )}

              {activeTab === 'holistic-reports' && (
                <>
                  <div className="mb-6">
                    <button
                      onClick={() => {
                        setEditingHolisticReport(null);
                        setShowHolisticReportCardForm(true);
                      }}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus size={18} className="mr-2" />
                      Generate Holistic Report
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {holisticReportCards.length === 0 ? (
                      <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
                        No holistic report cards found. Generate one to get started.
                      </div>
                    ) : (
                      holisticReportCards.map(card => (
                        <div key={card.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-2">
                              <BookOpen className="text-green-600" size={24} />
                              <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                  {card.studentData?.name || card.studentData?.displayName || 'Student'}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {card.class && `Class ${card.class}`} {card.section && `- ${card.section}`}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="mb-4 space-y-1">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Session:</span> {card.academicSession || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Type:</span> {card.reportType === 'exam' ? `Exam - ${card.examName || 'N/A'}` : 'Final Report'}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Percentage:</span> {card.summary?.percentage || '0'}%
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Grade:</span> {card.summary?.overallGrade || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                              Created: {card.createdAt?.toDate ? card.createdAt.toDate().toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDownloadHolisticReport(card)}
                              className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              <Download size={18} className="mr-2" />
                              Download PDF
                            </button>
                            <button
                              onClick={() => {
                                setEditingHolisticReport(card);
                                setShowHolisticReportCardForm(true);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                              title="Edit Report Card"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to delete this holistic report card?')) {
                                  try {
                                    await deleteHolisticReportCard(card.id);
                                    setHolisticReportCards(prev => prev.filter(c => c.id !== card.id));
                                    toast.success('Holistic report card deleted');
                                  } catch (error) {
                                    toast.error('Failed to delete report card');
                                  }
                                }
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                              title="Delete Report Card"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {activeTab === 'certificates' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCertificates.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
                      No certificates found
                    </div>
                  ) : (
                    filteredCertificates.map(cert => (
                      <div key={cert.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <Award className="text-yellow-600" size={24} />
                            <div>
                              <h3 className="font-semibold text-gray-900 dark:text-white">
                                {cert.studentData?.name || cert.studentData?.displayName || 'Student'}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                                {cert.certificateType}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                            {cert.title}
                          </p>
                          {cert.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {cert.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleDownloadCertificate(cert)}
                            className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Download size={18} className="mr-2" />
                            Download
                          </button>
                          <button
                            onClick={() => handleEditCertificate(cert)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                            title="Edit Certificate"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ show: true, id: cert.id, type: 'certificate' })}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                            title="Delete Certificate"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      )}

      {/* Generate Report Card Modal */}
      {showReportCardForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full p-6 max-h-[90vh] overflow-y-auto transition-all duration-300 ${showLivePreview ? 'max-w-6xl' : 'max-w-3xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Generate Report Card</h2>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowLivePreview(!showLivePreview)}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    showLivePreview 
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200'
                  }`}
                >
                  <Maximize2 size={16} className="mr-2" />
                  {showLivePreview ? 'Hide Preview' : 'Show Live Preview'}
                </button>
                <button
                  onClick={() => {
                    setShowReportCardForm(false);
                    setShowLivePreview(false);
                    setSelectedBatchForReportCard(null);
                    setStudents([]);
                    setReportCardForm({
                      studentId: '',
                      batchId: '',
                      academicYear: new Date().getFullYear(),
                      term: '',
                      subjects: [{ name: '', marks: '', maxMarks: '' }],
                      remarks: '',
                      templateId: 'modern-clean',
                      teacherName: '',
                      principalName: '',
                      rollNo: '',
                      attendance: ''
                    });
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className={`flex flex-col lg:flex-row gap-8 ${showLivePreview ? '' : 'justify-center'}`}>
              {/* Form Section */}
              <div className={`space-y-4 ${showLivePreview ? 'lg:w-1/2' : 'w-full'}`}>
                {/* Pre-select batch if one is already selected */}
                {selectedBatch && !selectedBatchForReportCard && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Using batch: <strong>{batches.find(b => b.id === selectedBatch)?.name}</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBatchForReportCard(selectedBatch);
                        loadStudents(selectedBatch);
                      }}
                      className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Use this batch
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Batch *
                  </label>
                  <select
                    value={selectedBatchForReportCard || ''}
                    onChange={(e) => handleBatchChangeForReportCard(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  >
                    <option value="">Select Batch</option>
                    {batches.map(batch => (
                      <option key={batch.id} value={batch.id}>
                        {batch.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Student *
                    </label>
                    <select
                      value={reportCardForm.studentId}
                      onChange={(e) => setReportCardForm({ ...reportCardForm, studentId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                      disabled={!selectedBatchForReportCard || loadingStudents}
                    >
                      <option value="">
                        {loadingStudents ? 'Loading students...' : selectedBatchForReportCard ? (students.length === 0 ? 'No students in this batch' : 'Select Student') : 'Please select a batch first'}
                      </option>
                      {students.length > 0 && students.map(student => (
                        <option key={student.id} value={student.id}>
                          {student.name || student.displayName || student.fullName || 'Unknown'} - {student.email || 'No email'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Academic Year
                    </label>
                    <input
                      type="number"
                      value={reportCardForm.academicYear}
                      onChange={(e) => setReportCardForm({ ...reportCardForm, academicYear: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Term
                  </label>
                  <select
                    value={reportCardForm.term}
                    onChange={(e) => setReportCardForm({ ...reportCardForm, term: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Term</option>
                    <option value="term1">Term 1</option>
                    <option value="term2">Term 2</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Subjects *
                    </label>
                    <button
                      type="button"
                      onClick={addSubject}
                      className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      + Add Subject
                    </button>
                  </div>
                  <div className="space-y-2">
                    {reportCardForm.subjects.map((subject, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2">
                        <input
                          type="text"
                          placeholder="Subject name"
                          value={subject.name}
                          onChange={(e) => updateSubject(index, 'name', e.target.value)}
                          className="col-span-5 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        <input
                          type="number"
                          placeholder="Marks"
                          value={subject.marks}
                          onChange={(e) => updateSubject(index, 'marks', e.target.value)}
                          className="col-span-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={subject.maxMarks}
                          onChange={(e) => updateSubject(index, 'maxMarks', e.target.value)}
                          className="col-span-3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        {reportCardForm.subjects.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSubject(index)}
                            className="col-span-1 text-red-600 hover:text-red-700"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Remarks
                  </label>
                  <textarea
                    value={reportCardForm.remarks}
                    onChange={(e) => setReportCardForm({ ...reportCardForm, remarks: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Select Template
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {reportCardTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setReportCardForm({ ...reportCardForm, templateId: template.id })}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                          reportCardForm.templateId === template.id 
                            ? 'border-blue-600 ring-2 ring-blue-100' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <img src={template.preview} alt={template.name} className="w-full h-24 object-cover" />
                        <div className="p-2 text-xs font-medium bg-white dark:bg-gray-800 text-center">
                          {template.name}
                        </div>
                        {reportCardForm.templateId === template.id && (
                          <div className="absolute top-1 right-1 bg-blue-600 text-white rounded-full p-1">
                            <CheckCircle2 size={12} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Professional Fields */}
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-3">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center">
                    <Layout className="w-4 h-4 mr-2" /> Professional Details
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Roll Number"
                      value={reportCardForm.rollNo}
                      onChange={(e) => setReportCardForm({ ...reportCardForm, rollNo: e.target.value })}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                    <input
                      type="text"
                      placeholder="Attendance (e.g. 95%)"
                      value={reportCardForm.attendance}
                      onChange={(e) => setReportCardForm({ ...reportCardForm, attendance: e.target.value })}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                    <input
                      type="text"
                      placeholder="Class Teacher Name"
                      value={reportCardForm.teacherName}
                      onChange={(e) => setReportCardForm({ ...reportCardForm, teacherName: e.target.value })}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                    <input
                      type="text"
                      placeholder="Principal Name"
                      value={reportCardForm.principalName}
                      onChange={(e) => setReportCardForm({ ...reportCardForm, principalName: e.target.value })}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Preview Section */}
              {showLivePreview && (
                <div className="lg:w-1/2 flex flex-col">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Live Template Preview</h3>
                    <span className="text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-bold uppercase">Dynamic</span>
                  </div>
                  <div className="flex-1 overflow-auto rounded-xl border-4 border-slate-100 dark:border-slate-700 shadow-2xl bg-slate-200 p-4">
                    <div className="origin-top scale-[0.85] transform-gpu">
                      <ReportCardPreview data={reportCardForm} templateId={reportCardForm.templateId} />
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-gray-500 italic text-center">
                    Note: This is a visual representation. The final PDF will be optimized for printing.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowReportCardForm(false);
                  setShowLivePreview(false);
                }}
                className="px-6 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateReportCard}
                className="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg font-bold flex items-center"
              >
                <FileText size={18} className="mr-2" />
                Generate & Save Report Card
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Generate Certificate Modal */}
      {showCertificateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full p-6 max-h-[90vh] overflow-y-auto transition-all duration-300 ${showLivePreview ? 'max-w-6xl' : 'max-w-xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Generate Certificate</h2>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowLivePreview(!showLivePreview)}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    showLivePreview 
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200'
                  }`}
                >
                  <Maximize2 size={16} className="mr-2" />
                  {showLivePreview ? 'Hide Preview' : 'Show Preview'}
                </button>
                <button
                  onClick={() => {
                    setShowCertificateForm(false);
                    setShowLivePreview(false);
                    setSelectedBatchForCertificate(null);
                    setStudents([]);
                    setCertificateForm({
                      studentId: '',
                      certificateType: 'achievement',
                      title: '',
                      description: '',
                      achievementDate: new Date().toISOString().split('T')[0],
                      templateId: 'classic-gold',
                      issuerName: '',
                      issuerTitle: '',
                      venue: ''
                    });
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className={`flex flex-col lg:flex-row gap-8 ${showLivePreview ? '' : 'justify-center'}`}>
              <div className={`space-y-4 ${showLivePreview ? 'lg:w-1/2' : 'w-full'}`}>
                {/* Pre-select batch if one is already selected */}
                {selectedBatch && !selectedBatchForCertificate && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Using batch: <strong>{batches.find(b => b.id === selectedBatch)?.name}</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBatchForCertificate(selectedBatch);
                        loadStudents(selectedBatch);
                      }}
                      className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Use this batch
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Batch *
                  </label>
                  <select
                    value={selectedBatchForCertificate || ''}
                    onChange={(e) => handleBatchChangeForCertificate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  >
                    <option value="">Select Batch</option>
                    {batches.map(batch => (
                      <option key={batch.id} value={batch.id}>
                        {batch.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Student *
                  </label>
                  <select
                    value={certificateForm.studentId}
                    onChange={(e) => setCertificateForm({ ...certificateForm, studentId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                    disabled={!selectedBatchForCertificate || loadingStudents}
                  >
                    <option value="">
                      {loadingStudents ? 'Loading students...' : selectedBatchForCertificate ? (students.length === 0 ? 'No students in this batch' : 'Select Student') : 'Please select a batch first'}
                    </option>
                    {students.length > 0 && students.map(student => (
                      <option key={student.id} value={student.id}>
                        {student.name || student.displayName || student.fullName || 'Unknown'} - {student.email || 'No email'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Certificate Type *
                  </label>
                  <select
                    value={certificateForm.certificateType}
                    onChange={(e) => setCertificateForm({ ...certificateForm, certificateType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="achievement">Achievement</option>
                    <option value="completion">Completion</option>
                    <option value="excellence">Excellence</option>
                    <option value="participation">Participation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={certificateForm.title}
                    onChange={(e) => setCertificateForm({ ...certificateForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Certificate of Excellence"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={certificateForm.description}
                    onChange={(e) => setCertificateForm({ ...certificateForm, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Description of achievement..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Achievement Date
                  </label>
                  <input
                    type="date"
                    value={certificateForm.achievementDate}
                    onChange={(e) => setCertificateForm({ ...certificateForm, achievementDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Select Template
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {certificateTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setCertificateForm({ ...certificateForm, templateId: template.id })}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                          certificateForm.templateId === template.id 
                            ? 'border-blue-600 ring-2 ring-blue-100' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <img src={template.preview} alt={template.name} className="w-full h-16 object-cover" />
                        <div className="p-1 text-[10px] font-medium bg-white dark:bg-gray-800 text-center">
                          {template.name}
                        </div>
                        {certificateForm.templateId === template.id && (
                          <div className="absolute top-1 right-1 bg-blue-600 text-white rounded-full p-1">
                            <CheckCircle2 size={10} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Professional Fields */}
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-3">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center">
                    <Layout className="w-4 h-4 mr-2" /> Professional Details
                  </h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Signatory Name"
                        value={certificateForm.issuerName}
                        onChange={(e) => setCertificateForm({ ...certificateForm, issuerName: e.target.value })}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                      />
                      <input
                        type="text"
                        placeholder="Signatory Title"
                        value={certificateForm.issuerTitle}
                        onChange={(e) => setCertificateForm({ ...certificateForm, issuerTitle: e.target.value })}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Venue / Place"
                      value={certificateForm.venue}
                      onChange={(e) => setCertificateForm({ ...certificateForm, venue: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Preview Section */}
              {showLivePreview && (
                <div className="lg:w-1/2 flex flex-col">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Live Certificate Preview</h3>
                  </div>
                  <div className="flex-1 overflow-auto rounded-xl border-4 border-slate-100 dark:border-slate-700 shadow-2xl bg-slate-200 p-4">
                    <div className="origin-top scale-[0.6] transform-gpu">
                      <CertificatePreview data={certificateForm} templateId={certificateForm.templateId} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowCertificateForm(false);
                  setShowLivePreview(false);
                }}
                className="px-6 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateCertificate}
                className="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg font-bold flex items-center"
              >
                <Award size={18} className="mr-2" />
                Generate & Save Certificate
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center text-red-600 mb-4">
              <AlertTriangle className="w-8 h-8 mr-3" />
              <h3 className="text-xl font-bold">Confirm Deletion</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this {deleteConfirm.type === 'report-card' ? 'report card' : 'certificate'}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, id: null, type: null })}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Permanently
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <HolisticReportCardForm 
        showForm={showHolisticReportCardForm}
        setShowForm={(show) => {
          setShowHolisticReportCardForm(show);
          if (!show) setEditingHolisticReport(null);
        }}
        batches={batches}
        students={students}
        loadStudents={loadStudents}
        userProfile={userProfile}
        editingReport={editingHolisticReport}
        onReportUpdated={(updatedReport) => {
          setHolisticReportCards(prev => 
            prev.map(r => r.id === updatedReport.id ? { ...r, ...updatedReport } : r)
          );
        }}
      />
    </div>
  );
};

export default ReportCards;
